import { useQuery } from '@apollo/client'
import { Skeleton, Statistic } from 'antd'
import { useRouter } from 'next/router'
import styled from 'styled-components'

import { Button } from '@/components/shared-components/BUI/Button'
import { useProject } from '@/context/projectContext'
import { useTemplateRenderer } from '@/context/templateRendererContext'
import { GET_PROJECT_HOURS_BUDGET_REPORT_FOR_SITE_DIARY } from '@/modules/reports/graphQL'
import ProjectBudgetStatistics from '@/modules/reports/ProjectBudgetStatistics'
import { GET_HOURS_BUDGET_SETTINGS } from '@/modules/settings/graphQL'
import { ProjectHoursBudgetDisplayBlock as ProjectHoursBudgetDisplayBlockType } from '@/modules/templates/types/blueprint'
import { routes } from '@/utils/index'

import {
  GetProjectHoursBudgetReportForSiteDiaryUiQuery,
  GetProjectHoursBudgetReportForSiteDiaryUiQueryVariables,
  HoursBudgetSettingsUiQuery,
  HoursBudgetSettingsUiQueryVariables,
} from 'types/api.graphql'

const NoTimeframeOrBudget = styled.div`
  display: flex;
  gap: 2.5rem;
  align-items: center;
  margin-top: 1rem;
`

type Props = {
  block: ProjectHoursBudgetDisplayBlockType
}

export const ProjectHoursBudgetDisplayBlock: React.FC<Props> = ({ block }) => {
  const router = useRouter()

  const { value } = block
  const { project } = useProject() ?? {}
  const { extraInfo, projectId, renderOptions } = useTemplateRenderer()

  const siteDiaryId = extraInfo.siteDiaryId as string

  const { data: settingsData, loading: settingsLoading } = useQuery<
    HoursBudgetSettingsUiQuery,
    HoursBudgetSettingsUiQueryVariables
  >(GET_HOURS_BUDGET_SETTINGS, {
    variables: {
      projectId,
    },
    skip: !projectId || renderOptions.renderingInPdf,
  })

  const { data: reportData, loading: reportLoading } = useQuery<
    GetProjectHoursBudgetReportForSiteDiaryUiQuery,
    GetProjectHoursBudgetReportForSiteDiaryUiQueryVariables
  >(GET_PROJECT_HOURS_BUDGET_REPORT_FOR_SITE_DIARY, {
    variables: {
      siteDiaryId,
    },
    fetchPolicy: 'network-only',
    skip:
      !settingsData?.hoursBudgetSettings?.hoursBudget ||
      renderOptions.renderingInPdf,
  })

  if (renderOptions.renderingInPdf) {
    return null
  }

  if (reportLoading || settingsLoading) return <Skeleton />

  return !settingsData?.hoursBudgetSettings?.hoursBudget ||
    !project.startDate ||
    !project.endDate ? (
    <NoTimeframeOrBudget>
      <Statistic title="Project completion %" value={value} />

      <Button
        onClick={() => router.push(routes.adminReportsProjectHoursBudget())}
      >
        Set project budget & timeframe
      </Button>
    </NoTimeframeOrBudget>
  ) : (
    <ProjectBudgetStatistics
      showTotalHours={true}
      projectCompletion={value}
      budget={settingsData?.hoursBudgetSettings?.hoursBudget}
      data={reportData?.getProjectHoursBudgetReportForSiteDiary}
      loading={reportLoading}
    />
  )
}
